import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { formatDate, formatTime, toBerlinDateKey } from '@brimo/shared';
import { clsx } from '../../../../ds/clsx';
import { Banner, Button, Skeleton, useToast } from '../../../../ds/components';
import { ApiError, errorMessage } from '../../../../lib/api';
import { useUserDirectory } from '../../../users/directory';
import { OrganizationPage, QueryError } from '../users/OrganizationPage';
import {
  searchNumbers,
  useCheckBundle,
  useJourney,
  usePurchaseNumber,
  useSaveAccount,
  useSaveBundle,
  useCoachingSettings,
  useSaveCoachingSettings,
  useSaveCredentials,
  useSetupNumbers,
  useTestCall,
  useTestMessage,
  type Journey,
  type JourneyStep,
  type NumberPurpose,
  type NumberSearchResult,
  type NumberSetupEntry,
} from './api';
import styles from './TelephonyPage.module.css';

const INTRO =
  'Für Anrufe und Vorlagen-Versand aus DAMOKLES brauchen Sie ein eigenes Twilio-Konto. Die Einrichtung dauert etwa 30 Minuten plus Prüfzeit bei Twilio.';
const COST_NOTE = 'Die Kosten werden direkt von Twilio abgerechnet, nicht über DAMOKLES.';
const TRUST_HUB_URL = 'https://console.twilio.com/us1/account/trust-hub/regulatory-compliance/bundles';
const BUNDLE_GUIDE_URL = 'https://help.twilio.com/articles/360017903653';

const purposeOptions: ReadonlyArray<{ value: NumberPurpose; label: string }> = [
  { value: 'caller_id', label: 'Anrufe (Caller-ID)' },
  { value: 'sms', label: 'SMS' },
  { value: 'inbound', label: 'Eingehende Anrufe' },
];

/** „heute 14:48" or „12.09.2026 · 14:48". */
function whenLabel(iso: string): string {
  const today = toBerlinDateKey(new Date());
  return toBerlinDateKey(iso) === today
    ? `heute ${formatTime(iso)}`
    : `${formatDate(iso)} · ${formatTime(iso)}`;
}

/** Field errors of a 400 or the message of any other failure. */
function failureText(error: unknown, field: string): string {
  if (error instanceof ApiError) return error.fields[field] ?? error.message;
  return errorMessage(error);
}

/**
 * „Telefonie und Nachrichtenversand" (Einstellungen 25/26): the six steps of the Twilio journey with progress
 * and badge. Done steps show their meta, the active step its form, locked steps are dashed. While the bundle is
 * checked by Twilio (or rejected) the dark status row, the rejection box with Twilio's reason and the number
 * purchase card of Einstellungen 26 replace the bundle form. Every answer of the server is the fresh journey.
 */
export function TelephonyPage() {
  const journey = useJourney();
  const data = journey.data;
  return (
    <OrganizationPage
      title="Telefonie und Nachrichtenversand"
      description={INTRO}
      actions={data ? <span className={styles.badge}>{data.badge}</span> : null}
    >
      {journey.isError ? <QueryError error={journey.error} /> : null}
      {journey.isPending ? (
        <div className={styles.skeleton} aria-busy="true">
          <Skeleton variant="block" height={6} />
          <Skeleton variant="block" height={76} />
          <Skeleton variant="block" height={76} />
          <Skeleton variant="block" height={220} />
        </div>
      ) : null}
      {data ? <JourneyView journey={data} /> : null}
      {data ? <CoachingCard /> : null}
    </OrganizationPage>
  );
}

function JourneyView({ journey }: { journey: Journey }) {
  const bundleStep = journey.steps.find((step) => step.key === 'bundle');
  const bundlePending =
    bundleStep?.status === 'active' && journey.bundle !== null && journey.bundle.status !== 'approved';
  return (
    <>
      <div
        className={styles.progress}
        role="progressbar"
        aria-label="Fortschritt der Einrichtung"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={journey.progressPct}
      >
        <div className={styles.progressFill} style={{ width: `${journey.progressPct}%` }} />
      </div>
      <div className={styles.steps}>
        {journey.steps.map((step) => {
          if (step.key === 'bundle' && bundlePending)
            return <BundleStatus key={step.key} journey={journey} step={step} />;
          if (step.key === 'number' && (step.status === 'active' || bundlePending)) {
            return <PurchaseCard key={step.key} journey={journey} step={step} />;
          }
          if (step.status === 'active') return <ActiveStep key={step.key} journey={journey} step={step} />;
          if (step.status === 'done') return <DoneStep key={step.key} journey={journey} step={step} />;
          return <LockedStep key={step.key} step={step} />;
        })}
      </div>
    </>
  );
}

function StepCircle({ step }: { step: JourneyStep }) {
  if (step.status === 'done') {
    return (
      <span className={clsx(styles.circle, styles.circleDone)} aria-label="erledigt">
        ✓
      </span>
    );
  }
  return (
    <span
      className={clsx(styles.circle, step.status === 'active' ? styles.circleActive : styles.circleLocked)}
    >
      {step.number}
    </span>
  );
}

function LockedStep({ step }: { step: JourneyStep }) {
  const search = step.actions['search'];
  return (
    <section
      className={clsx(styles.step, styles.stepLocked)}
      aria-label={`Schritt ${step.number}: ${step.title}`}
    >
      <StepCircle step={step} />
      <div className={styles.stepText}>
        <h3 className={styles.stepTitle}>{step.title}</h3>
        <span className={styles.stepMeta}>{step.text}</span>
      </div>
      {search ? (
        <button type="button" className={styles.smallButton} disabled title={search.reason ?? undefined}>
          Nummer suchen
        </button>
      ) : null}
    </section>
  );
}

function DoneStep({ journey, step }: { journey: Journey; step: JourneyStep }) {
  const [open, setOpen] = useState(false);
  const save = step.actions['save'];
  let control: ReactNode = step.doneBy ? <span className={styles.doneBy}>{step.doneBy}</span> : null;
  let editor: ReactNode = null;
  if (step.key === 'credentials' && save?.allowed) {
    control = open ? null : (
      <button type="button" className={styles.smallButton} onClick={() => setOpen(true)}>
        Token ersetzen
      </button>
    );
    editor = open ? <CredentialsForm replace onDone={() => setOpen(false)} /> : null;
  }
  if (step.key === 'setup' && save?.allowed && journey.numbers.length > 0) {
    control = open ? null : (
      <button type="button" className={styles.smallButton} onClick={() => setOpen(true)}>
        Zuweisung ändern
      </button>
    );
    editor = open ? <SetupForm journey={journey} onDone={() => setOpen(false)} /> : null;
  }
  if (step.key === 'test' && step.actions['call']?.allowed) {
    control = open ? null : (
      <button type="button" className={styles.smallButton} onClick={() => setOpen(true)}>
        Erneut testen
      </button>
    );
    editor = open ? <TestForms journey={journey} step={step} /> : null;
  }
  return (
    <section
      className={clsx(styles.step, editor !== null && styles.stepWrap)}
      aria-label={`Schritt ${step.number}: ${step.title}`}
    >
      <StepCircle step={step} />
      <div className={styles.stepText}>
        <h3 className={styles.stepTitle}>{step.title}</h3>
        {step.meta ? <span className={styles.stepMeta}>{step.meta}</span> : null}
      </div>
      {control}
      {editor ? <div style={{ flexBasis: '100%', paddingLeft: 46 }}>{editor}</div> : null}
    </section>
  );
}

function ActiveStep({ journey, step }: { journey: Journey; step: JourneyStep }) {
  let body: ReactNode = null;
  if (step.key === 'account') body = <AccountForm />;
  if (step.key === 'credentials') body = <CredentialsForm />;
  if (step.key === 'bundle') body = <BundleForm journey={journey} step={step} />;
  if (step.key === 'setup') body = <SetupForm journey={journey} />;
  if (step.key === 'test') body = <TestForms journey={journey} step={step} />;
  return (
    <section
      className={styles.active}
      aria-label={`Schritt ${step.number}: ${step.title}`}
      aria-current="step"
    >
      <StepCircle step={step} />
      <div className={styles.activeBody}>
        <div className={styles.stepText}>
          <h3 className={styles.activeTitle}>{step.title}</h3>
          <span className={styles.activeText}>{step.text}</span>
        </div>
        {body}
      </div>
    </section>
  );
}

/** A labelled single-line field of the journey forms (labels in the frame's eyebrow style). */
function Field({
  label,
  value,
  onChange,
  error,
  type = 'text',
  placeholder,
  autoComplete = 'off',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | null | undefined;
  type?: 'text' | 'password' | 'tel' | undefined;
  placeholder?: string | undefined;
  autoComplete?: string | undefined;
}) {
  const id = useId();
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={clsx(styles.fieldLabel, error && styles.fieldLabelError)}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        className={clsx(styles.input, error && styles.inputError)}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        spellCheck={false}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <span id={`${id}-error`} className={styles.fieldError}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

function AccountForm() {
  const { toast } = useToast();
  const save = useSaveAccount();
  const [sid, setSid] = useState('');
  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate(sid.trim(), { onSuccess: () => toast({ message: 'Account SID gespeichert.' }) });
  }
  return (
    <form className={styles.inline} onSubmit={submit} noValidate>
      <Field
        label="Account SID"
        value={sid}
        onChange={setSid}
        placeholder="AC…"
        error={save.isError ? failureText(save.error, 'accountSid') : null}
      />
      <Button type="submit" size="small" loading={save.isPending} disabled={sid.trim() === ''}>
        Speichern
      </Button>
    </form>
  );
}

function CredentialsForm({ replace = false, onDone }: { replace?: boolean; onDone?: () => void }) {
  const { toast } = useToast();
  const save = useSaveCredentials();
  const [token, setToken] = useState('');
  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate(token.trim(), {
      onSuccess: () => {
        toast({ message: 'Auth Token geprüft und gespeichert.' });
        setToken('');
        onDone?.();
      },
    });
  }
  return (
    <form className={styles.inline} onSubmit={submit} noValidate>
      <Field
        label="Auth Token"
        type="password"
        autoComplete="new-password"
        value={token}
        onChange={setToken}
        error={save.isError ? failureText(save.error, 'authToken') : null}
      />
      <Button type="submit" size="small" loading={save.isPending} disabled={token.trim() === ''}>
        {replace ? 'Token ersetzen' : 'Prüfen und speichern'}
      </Button>
      {replace && onDone ? (
        <Button type="button" variant="secondary" size="small" onClick={onDone}>
          Abbrechen
        </Button>
      ) : null}
    </form>
  );
}

function BundleSidForm({ onCancel }: { onCancel?: () => void }) {
  const { toast } = useToast();
  const save = useSaveBundle();
  const [sid, setSid] = useState('');
  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate(sid.trim(), {
      onSuccess: () => toast({ message: 'Bundle-SID gespeichert. DAMOKLES fragt den Status stündlich ab.' }),
    });
  }
  return (
    <form className={styles.inline} onSubmit={submit} noValidate>
      <Field
        label="Bundle-SID"
        value={sid}
        onChange={setSid}
        placeholder="BU…"
        error={save.isError ? failureText(save.error, 'bundleSid') : null}
      />
      <Button type="submit" size="small" loading={save.isPending} disabled={sid.trim() === ''}>
        Speichern
      </Button>
      {onCancel ? (
        <Button type="button" variant="secondary" size="small" onClick={onCancel}>
          Abbrechen
        </Button>
      ) : null}
    </form>
  );
}

function BundleForm({ journey, step }: { journey: Journey; step: JourneyStep }) {
  const navigate = useNavigate();
  const [entering, setEntering] = useState(false);
  const save = step.actions['save'];
  const document = journey.bundle?.documents[0] ?? null;
  return (
    <>
      <div className={styles.fields}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Firmenname laut Handelsregister</span>
          <div className={styles.readonly}>{journey.companyName}</div>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Nachweis der Adresse</span>
          <div className={styles.document}>
            {document ? (
              <>
                {document.friendlyName}
                <span className={styles.documentMeta}>{document.status}</span>
              </>
            ) : (
              <span className={styles.documentEmpty}>Liest DAMOKLES aus dem Bundle bei Twilio</span>
            )}
          </div>
        </div>
      </div>
      <div className={styles.guide}>
        <span className={styles.guideTitle}>So gehen Sie vor</span>
        <ol className={styles.guideList}>
          <li>
            1. In der Twilio Console{' '}
            <a href={TRUST_HUB_URL} target="_blank" rel="noreferrer">
              Trust Hub · Regulatory Compliance
            </a>{' '}
            öffnen.
          </li>
          <li>2. Neues Bundle für Deutschland, Typ „Local", Nutzung „Business" anlegen.</li>
          <li>
            3. Handelsregisterauszug und Adressnachweis hochladen —{' '}
            <a href={BUNDLE_GUIDE_URL} target="_blank" rel="noreferrer">
              Twilio-Anleitung zu Regulatory Bundles
            </a>
            .
          </li>
          <li>4. Bundle einreichen und die Bundle-SID hier eintragen.</li>
        </ol>
      </div>
      {entering ? <BundleSidForm onCancel={() => setEntering(false)} /> : null}
      <div className={styles.footer}>
        <span className={styles.footerNote}>Prüfung durch Twilio dauert in der Regel 1 bis 3 Werktage.</span>
        {entering ? null : (
          <span className={styles.footerActions}>
            <Button variant="secondary" size="small" onClick={() => void navigate('/tasks')}>
              Später fortsetzen
            </Button>
            <Button
              size="small"
              disabled={!save?.allowed}
              title={save?.reason ?? undefined}
              onClick={() => setEntering(true)}
            >
              Bundle-SID eintragen
            </Button>
          </span>
        )}
      </div>
    </>
  );
}

function BundleStatus({ journey, step }: { journey: Journey; step: JourneyStep }) {
  const { toast } = useToast();
  const check = useCheckBundle();
  const bundle = journey.bundle;
  if (!bundle) return null;
  const checkAction = step.actions['check'];
  const submitted = bundle.submittedAt ? `Eingereicht ${whenLabel(bundle.submittedAt)} · ` : '';
  const title =
    bundle.status === 'waiting'
      ? `Bundle ${bundle.sidMasked} wird von Twilio geprüft`
      : bundle.status === 'open'
        ? `Bundle ${bundle.sidMasked} ist bei Twilio noch ein Entwurf`
        : `Bundle ${bundle.sidMasked} wurde abgelehnt`;
  const meta =
    bundle.status === 'waiting'
      ? `${submitted}Twilio meldet sich per E-Mail · DAMOKLES fragt den Status stündlich ab`
      : bundle.status === 'open'
        ? 'Reichen Sie das Bundle in der Twilio Console ein · DAMOKLES fragt den Status stündlich ab'
        : 'Reichen Sie es in der Twilio Console erneut ein · DAMOKLES fragt den Status stündlich ab';
  const rejection = bundle.rejection;
  return (
    <>
      <section className={styles.waiting} aria-label={title} role="status">
        {bundle.status === 'rejected' ? null : <span className={styles.spinner} aria-hidden="true" />}
        <div className={styles.stepText}>
          <h3 className={styles.waitingTitle}>{title}</h3>
          <span className={styles.waitingMeta}>{meta}</span>
        </div>
        <span className={styles.waitingPill}>{bundle.statusLabel}</span>
        <button
          type="button"
          className={styles.waitingButton}
          disabled={!checkAction?.allowed || check.isPending}
          title={checkAction?.reason ?? undefined}
          onClick={() =>
            check.mutate(undefined, {
              onSuccess: (fresh) =>
                toast({ message: `Twilio meldet: ${fresh.bundle?.statusLabel ?? 'unverändert'}.` }),
            })
          }
        >
          Jetzt prüfen
        </button>
      </section>
      {check.isError ? <Banner tone="danger">{errorMessage(check.error)}</Banner> : null}
      {rejection ? (
        <section className={styles.rejection} aria-label="Ablehnung durch Twilio">
          <div className={styles.rejectionText}>
            <h3 className={styles.rejectionTitle}>
              {rejection.previous ? 'Vorheriger Versuch wurde abgelehnt' : 'Twilio hat das Bundle abgelehnt'}:
              „{rejection.reason}“
            </h3>
            <p className={styles.rejectionBody}>
              {rejection.explanation}{' '}
              <a href={BUNDLE_GUIDE_URL} target="_blank" rel="noreferrer">
                Anforderungen bei Twilio nachlesen
              </a>
            </p>
          </div>
          <a className={styles.rejectionButton} href={bundle.consoleUrl} target="_blank" rel="noreferrer">
            Dokument tauschen
          </a>
        </section>
      ) : null}
    </>
  );
}

function PurchaseCard({ journey, step }: { journey: Journey; step: JourneyStep }) {
  const { toast } = useToast();
  const purchase = usePurchaseNumber();
  const [type, setType] = useState<'local' | 'mobile'>('local');
  const [areaCode, setAreaCode] = useState('');
  const [results, setResults] = useState<NumberSearchResult[] | null>(null);
  const [costNote, setCostNote] = useState(COST_NOTE);
  const [selected, setSelected] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locked = step.status === 'locked';
  const search = step.actions['search'];
  const buy = step.actions['purchase'];
  const searchAllowed = !locked && (search?.allowed ?? journey.search.enabled);
  const canBuy = !locked && selected !== null && journey.purchaseEnabled && (buy?.allowed ?? false);
  const reason = locked
    ? (journey.search.lockedReason ?? step.text)
    : !journey.purchaseEnabled
      ? journey.purchaseReason
      : null;

  async function runSearch(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSearching(true);
    try {
      const answer = await searchNumbers(type, areaCode);
      setResults(answer.results);
      setCostNote(answer.costNote);
      setSelected(null);
    } catch (failure) {
      setError(failureText(failure, 'areaCode'));
    } finally {
      setSearching(false);
    }
  }

  function buyNumber() {
    if (!selected) return;
    setError(null);
    purchase.mutate(selected, {
      onSuccess: () => toast({ message: 'Rufnummer gekauft.' }),
      onError: (failure) => setError(errorMessage(failure)),
    });
  }

  return (
    <section
      className={clsx(styles.purchase, !locked && styles.purchaseActive)}
      aria-label={`Schritt ${step.number}: ${step.title}`}
    >
      <div className={styles.purchaseHead}>
        <h3 className={styles.purchaseTitle}>Schritt {step.number} · Rufnummer kaufen</h3>
        {locked ? <span className={styles.lockedPill}>gesperrt bis Bundle bestätigt</span> : null}
      </div>
      <form className={styles.searchRow} onSubmit={(event) => void runSearch(event)} noValidate>
        <select
          aria-label="Land und Nummerntyp"
          className={styles.searchField}
          value={type}
          disabled={!searchAllowed}
          onChange={(event) => setType(event.target.value === 'mobile' ? 'mobile' : 'local')}
        >
          <option value="local">Deutschland · +49</option>
          <option value="mobile">Deutschland · Mobil</option>
        </select>
        <input
          aria-label="Vorwahl"
          className={styles.searchField}
          placeholder="Vorwahl, z. B. 941"
          inputMode="numeric"
          value={areaCode}
          disabled={!searchAllowed || type === 'mobile'}
          onChange={(event) => setAreaCode(event.target.value)}
        />
        <button type="submit" className={styles.searchButton} disabled={!searchAllowed || searching}>
          Suchen
        </button>
      </form>
      {results ? (
        <div
          className={clsx(styles.results, locked && styles.resultsLocked)}
          role="radiogroup"
          aria-label="Gefundene Nummern"
        >
          <div className={clsx(styles.resultRow, styles.resultHead)} aria-hidden="true">
            <span />
            <span>Nummer</span>
            <span>Typ</span>
            <span>Fähigkeiten</span>
            <span>Preis</span>
          </div>
          {results.length === 0 ? (
            <div className={styles.resultEmpty}>Keine freien Nummern gefunden.</div>
          ) : null}
          {results.map((result) => (
            <label key={result.e164} className={styles.resultRow}>
              <input
                type="radio"
                name="telephony-number"
                value={result.e164}
                checked={selected === result.e164}
                onChange={() => setSelected(result.e164)}
              />
              <span>{result.display}</span>
              <span>{result.typeLabel}</span>
              <span>{result.capabilitiesLabel}</span>
              <span>{result.priceLabel ?? '—'}</span>
            </label>
          ))}
        </div>
      ) : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <div className={styles.footer}>
        <span className={styles.footerNote}>{costNote}</span>
        <span className={styles.footerActions}>
          <Button size="small" disabled={!canBuy} loading={purchase.isPending} onClick={buyNumber}>
            Nummer kaufen
          </Button>
        </span>
      </div>
      {reason ? <span className={styles.reason}>{reason}</span> : null}
    </section>
  );
}

function SetupForm({ journey, onDone }: { journey: Journey; onDone?: () => void }) {
  const { toast } = useToast();
  const setup = useSetupNumbers();
  const directory = useUserDirectory();
  const [rows, setRows] = useState<NumberSetupEntry[]>([]);

  useEffect(() => {
    setRows(
      journey.numbers
        .filter((number) => number.active)
        .map((number) => ({
          phoneNumberId: number.id,
          purpose: number.purpose,
          assignedUserId: number.assignedUser?.id ?? null,
          poolLabel: number.poolLabel,
        })),
    );
  }, [journey.numbers]);

  function update(index: number, patch: Partial<NumberSetupEntry>) {
    setRows((current) => current.map((row, position) => (position === index ? { ...row, ...patch } : row)));
  }

  function submit() {
    setup.mutate(rows, {
      onSuccess: () => {
        toast({ message: 'Nummern zugewiesen, Webhooks gesetzt.' });
        onDone?.();
      },
    });
  }

  const users = directory.data ?? [];
  return (
    <>
      <div className={styles.assign} role="table" aria-label="Rufnummern zuweisen">
        {rows.map((row, index) => {
          const number = journey.numbers.find((entry) => entry.id === row.phoneNumberId);
          if (!number) return null;
          return (
            <div key={row.phoneNumberId} className={styles.assignRow} role="row">
              <span role="cell">
                {number.display}
                <span className={styles.assignMeta}>{number.capabilitiesLabel}</span>
              </span>
              <span role="cell">
                <select
                  aria-label={`Zweck ${number.display}`}
                  className={styles.input}
                  value={row.purpose}
                  onChange={(event) => update(index, { purpose: event.target.value as NumberPurpose })}
                >
                  {purposeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </span>
              <span role="cell">
                <select
                  aria-label={`Nutzer ${number.display}`}
                  className={styles.input}
                  value={row.assignedUserId ?? ''}
                  onChange={(event) => update(index, { assignedUserId: event.target.value || null })}
                >
                  <option value="">Pool (alle)</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </span>
              <span role="cell">
                <input
                  aria-label={`Pool-Bezeichnung ${number.display}`}
                  className={styles.input}
                  placeholder="Pool-Bezeichnung"
                  value={row.poolLabel ?? ''}
                  disabled={row.assignedUserId !== null}
                  onChange={(event) => update(index, { poolLabel: event.target.value || null })}
                />
              </span>
            </div>
          );
        })}
      </div>
      {setup.isError ? <Banner tone="danger">{errorMessage(setup.error)}</Banner> : null}
      <div className={styles.footer}>
        <span className={styles.footerNote}>DAMOKLES setzt die Webhooks automatisch</span>
        <span className={styles.footerActions}>
          {onDone ? (
            <Button variant="secondary" size="small" onClick={onDone}>
              Abbrechen
            </Button>
          ) : null}
          <Button size="small" disabled={rows.length === 0} loading={setup.isPending} onClick={submit}>
            Zuweisen und Webhooks setzen
          </Button>
        </span>
      </div>
    </>
  );
}

function TestForms({ journey, step }: { journey: Journey; step: JourneyStep }) {
  const call = useTestCall();
  const message = useTestMessage();
  const [callTo, setCallTo] = useState('');
  const [messageTo, setMessageTo] = useState('');
  const callAction = step.actions['call'];
  const messageAction = step.actions['message'];
  return (
    <>
      <form
        className={styles.inline}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          call.mutate(callTo);
        }}
      >
        <Field
          label="Testanruf an"
          type="tel"
          value={callTo}
          onChange={setCallTo}
          placeholder="Ihre Mobilnummer"
          error={call.isError ? failureText(call.error, 'to') : null}
        />
        <Button type="submit" size="small" loading={call.isPending} disabled={!callAction?.allowed}>
          Testanruf starten
        </Button>
      </form>
      {call.data ? <span className={styles.success}>Testanruf an {call.data.to} gestartet.</span> : null}
      {!call.data && journey.testCallAt ? (
        <span className={styles.success}>Letzter Testanruf {whenLabel(journey.testCallAt)}.</span>
      ) : null}
      <form
        className={styles.inline}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          message.mutate(messageTo);
        }}
      >
        <Field
          label="Testnachricht an"
          type="tel"
          value={messageTo}
          onChange={setMessageTo}
          placeholder="+49 …"
          error={message.isError ? failureText(message.error, 'to') : null}
        />
        <Button
          type="submit"
          size="small"
          variant="secondary"
          loading={message.isPending}
          disabled={!messageAction?.allowed || messageTo.trim() === ''}
        >
          Testnachricht senden
        </Button>
      </form>
      {message.data ? (
        <span className={styles.success}>Testnachricht an {message.data.to} gesendet.</span>
      ) : null}
      {!message.data && journey.testMessageAt ? (
        <span className={styles.success}>Letzte Testnachricht {whenLabel(journey.testMessageAt)}.</span>
      ) : null}
    </>
  );
}

const COACHING_HINT =
  'Nach jedem Gespräch ab 60 Sekunden wird ein Coaching-Feedback in diese Gruppe gepostet.';
const COACHING_BRIDGE_MISSING =
  'Die WhatsApp-Anbindung für das Coaching ist auf dem Server nicht eingerichtet — das Feedback wird am Gespräch gespeichert, aber nicht gepostet.';

/** „Coaching-Gruppe (WhatsApp-Chat-ID)" below the journey (D-226): the group of the post-call coaching. */
function CoachingCard() {
  const { toast } = useToast();
  const settings = useCoachingSettings();
  const save = useSaveCoachingSettings();
  const [chatId, setChatId] = useState<string | null>(null);
  const stored = settings.data?.chatId ?? '';
  const value = chatId ?? stored;
  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate(value, {
      onSuccess: () => {
        setChatId(null);
        toast({ message: 'Coaching-Gruppe gespeichert.' });
      },
    });
  }
  if (settings.isError) return <QueryError error={settings.error} />;
  if (!settings.data) return null;
  return (
    <section className={styles.coaching} aria-labelledby="telephony-coaching-title">
      <div id="telephony-coaching-title" className={styles.activeTitle}>
        Coaching-Gruppe (WhatsApp-Chat-ID)
      </div>
      <p className={styles.activeText}>{COACHING_HINT}</p>
      {!settings.data.bridgeConfigured ? <Banner tone="warning">{COACHING_BRIDGE_MISSING}</Banner> : null}
      <form className={styles.inline} onSubmit={submit} noValidate>
        <Field
          label="WhatsApp-Chat-ID"
          value={value}
          onChange={setChatId}
          placeholder="…@g.us"
          error={save.isError ? failureText(save.error, 'chatId') : null}
        />
        <Button type="submit" size="small" loading={save.isPending} disabled={value.trim() === stored}>
          Speichern
        </Button>
      </form>
    </section>
  );
}
